/**
 * RequestStatus — the canonical async-request status vocabulary.
 *
 * The store speaks at least four dialects for "it worked" (`success`,
 * `successful`, `complete`, `completed`) and free-typed strings for the rest —
 * which is how the `status === "loadng"` bug (roadmap Module 0) shipped.
 * New slices SHOULD store REQUEST_STATUS values; existing slices keep their
 * wire dialects and callers normalize through the predicates below.
 *
 * Roadmap: docs/domain-refactor-roadmap.md (Module 6). Linear: INH-5151.
 */

export const REQUEST_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  FAILED: "failed",
} as const;

export type RequestStatus = (typeof REQUEST_STATUS)[keyof typeof REQUEST_STATUS];

const SUCCESS_DIALECTS: readonly string[] = ["success", "successful", "complete", "completed", "settled"];
const LOADING_DIALECTS: readonly string[] = ["loading", "pending", "in_progress"];
const FAILED_DIALECTS: readonly string[] = ["failed", "error", "failure"];

/** True for every dialect of "it worked" found in the store. */
export const isSuccessStatus = (status: string | null | undefined): boolean =>
  !!status && SUCCESS_DIALECTS.includes(status);

/** True for every dialect of "in flight". */
export const isLoadingStatus = (status: string | null | undefined): boolean =>
  !!status && LOADING_DIALECTS.includes(status);

/** True for every dialect of "it failed". */
export const isFailedStatus = (status: string | null | undefined): boolean =>
  !!status && FAILED_DIALECTS.includes(status);

/** Collapse any dialect to the canonical vocabulary; unknown/absent → IDLE. */
export const normalizeRequestStatus = (status: string | null | undefined): RequestStatus => {
  if (isSuccessStatus(status)) return REQUEST_STATUS.SUCCESS;
  if (isLoadingStatus(status)) return REQUEST_STATUS.LOADING;
  if (isFailedStatus(status)) return REQUEST_STATUS.FAILED;
  return REQUEST_STATUS.IDLE;
};
